/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';

import { MessageItem } from 'ajs/modules/data-model/factories';
import { BotDetectionMatchConfigItem } from 'ajs/modules/match';
import { BotDetectionMatch } from 'object-types';
import { BotClassificationTypes, IBotClassification } from 'generated-types';

import { IAviDataGridConfig } from 'ng/modules/data-grid';

import * as globalL10n from 'global-l10n';
import * as l10n from './bot-management-match.l10n';

import './bot-management-match.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Reusable component for security/request rules for BotDetectionMatch
 * @author Umesh Chovatiya
 */

@Component({
    selector: 'bot-management-match',
    templateUrl: './bot-management-match.component.html',
})
export class BotManagementMatchComponent {
    /**
     * Template ref for classification field.
     */
    @ViewChild('classificationTemplateRef')
    public readonly classificationTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for classification value field.
     */
    @ViewChild('valueTemplateRef')
    public readonly valueTemplateRef: TemplateRef<HTMLElement>;

    /**
     * BotDetectionMatchConfigItem ObjectTypeItem Instance
     */
    @Input()
    public editable: BotDetectionMatchConfigItem;

    /**
     * Index of the match within the MatchAdder component.
     */
    @Input()
    public matchIndex = 0;

    /**
     * Match label passed to the MatchWrapperComponent
     */
    @Input()
    public label: string;

    /**
     * fieldName passed to the MatchWrapperComponent
     */
    @Input()
    public fieldName: string;

    /**
     * EventEmitter for removing a match, passed to the MatchWrapperComponent
     */
    @Output()
    public onRemoveMatch = new EventEmitter<void>();

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Grid Config for Classifications.
     */
    public classificationGridConfig: IAviDataGridConfig;

    /**
     * Object type for BotDetectionMatch.
     */
    public objectType = BotDetectionMatch;

    /**
     * Local Hash for botClassificationType to check for User-defined Bot.
     */
    public botClassificationTypesHash = {
        [BotClassificationTypes.USER_DEFINED_BOT]: BotClassificationTypes.USER_DEFINED_BOT,
    };

    /**
     * Used for checking User Defined Bot classifications type in template.
     */
    public readonly userDefinedBot = BotClassificationTypes.USER_DEFINED_BOT;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const {
            l10nService,
            classificationTemplateRef,
            valueTemplateRef,
            editable,
        } = this;

        this.classificationGridConfig = {
            fields: [
                {
                    id: 'classification',
                    label: l10nService.getMessage(l10nKeys.classificationLabel),
                    sortBy: 'classification',
                    templateRef: classificationTemplateRef,
                }, {
                    id: 'value',
                    label: l10nService.getMessage(globalL10nKeys.valueLabel),
                    sortBy: 'value',
                    templateRef: valueTemplateRef,
                },
            ],
            layout: {
                placeholderMessage: l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
            singleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    shape: 'trash',
                    onClick: (
                        row: MessageItem<IBotClassification>,
                    ) => editable.removeClassification(row),
                },
            ],
            multipleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    onClick: (
                        selectedBotClassifications: Array<MessageItem<IBotClassification>>,
                    ) => {
                        selectedBotClassifications.forEach((
                            classification: MessageItem<IBotClassification>,
                        ) => editable.removeClassification(classification));
                    },
                },
            ],
        };
    }

    /**
     * Called to remove this match. Passed to the MatchWrapperComponent.
     */
    public removeMatch(): void {
        this.onRemoveMatch.emit();
    }
}
